import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Empty, Spin, message } from 'antd';
import {
  fetchAiCoachConversations,
  fetchAiCoachMessages,
  normalizeRows,
} from '../../../core_units/api/ai-coach';
import ConversationSidebar from './ConversationSidebar';
import ChatTranscript from './ChatTranscript';
import './ai-coach.css';

export const extractApiError = (response) => response?.data?.error ?? null;

export const getFriendlyErrorMessage = (error) => {
  const statusCode = error?.statusCode;
  if (statusCode === 403) {
    return 'You do not have permission to view AI Coach conversations.';
  }
  if (statusCode === 500) {
    return 'Something went wrong loading AI Coach data. Please try again.';
  }
  return error?.message || 'Unable to load AI Coach data. Please try again.';
};

const AiCoachPage = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [conversationsLoading, setConversationsLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const messagesRequestId = useRef(0);

  const loadConversations = useCallback(async ({ preserveSelection = false } = {}) => {
    setConversationsLoading(true);
    try {
      const response = await fetchAiCoachConversations();
      const error = extractApiError(response);
      if (error) {
        message.error(getFriendlyErrorMessage(error));
        setConversations([]);
        setSelectedConversationId(null);
        setMessages([]);
        return;
      }

      const rows = normalizeRows(response);
      setConversations(rows);

      if (rows.length === 0) {
        setSelectedConversationId(null);
        return;
      }

      setSelectedConversationId((current) => {
        if (preserveSelection && rows.some((row) => row.conversation_id === current)) {
          return current;
        }
        return rows[0].conversation_id;
      });
    } catch {
      message.error('Unable to load conversations. Please try again.');
      setConversations([]);
      setSelectedConversationId(null);
      setMessages([]);
    } finally {
      setConversationsLoading(false);
    }
  }, []);

  const loadMessages = useCallback(async (conversationId) => {
    if (!conversationId) {
      setMessages([]);
      return;
    }

    const requestId = ++messagesRequestId.current;
    setMessages([]);
    setMessagesLoading(true);

    try {
      const response = await fetchAiCoachMessages(conversationId);
      if (requestId !== messagesRequestId.current) return;

      const error = extractApiError(response);
      if (error) {
        message.error(getFriendlyErrorMessage(error));
        setMessages([]);
        return;
      }

      setMessages(normalizeRows(response));
    } catch {
      if (requestId !== messagesRequestId.current) return;
      message.error('Unable to load messages. Please try again.');
      setMessages([]);
    } finally {
      if (requestId === messagesRequestId.current) {
        setMessagesLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  useEffect(() => {
    loadMessages(selectedConversationId);
  }, [selectedConversationId, loadMessages]);

  const handleRefresh = () => {
    loadConversations({ preserveSelection: true });
    if (selectedConversationId) {
      loadMessages(selectedConversationId);
    }
  };

  const selectedConversation = conversations.find(
    (c) => c.conversation_id === selectedConversationId
  );

  const showEmpty = !conversationsLoading && conversations.length === 0;

  return (
    <div className="HomePageContainer ai-coach-page">
      <h1 className="ai-coach-title">AI Coach</h1>
      {conversationsLoading && conversations.length === 0 ? (
        <div className="ai-coach-loading">
          <Spin size="large" />
        </div>
      ) : showEmpty ? (
        <div className="ai-coach-empty">
          <Button onClick={() => loadConversations()}>Refresh</Button>
          <Empty description="No AI Coach conversations yet" />
        </div>
      ) : (
        <div className="ai-coach-layout">
          <ConversationSidebar
            conversations={conversations}
            selectedId={selectedConversationId}
            onSelect={setSelectedConversationId}
            loading={conversationsLoading}
            onRefresh={handleRefresh}
          />
          <main className="ai-coach-main">
            <ChatTranscript
              messages={messages}
              loading={messagesLoading}
              conversation={selectedConversation}
            />
          </main>
        </div>
      )}
    </div>
  );
};

export default AiCoachPage;
